// JSA Training classes
import { v4 as uuidv4 } from "uuid";

// JSA Training Employees Info
export class JsaTrainingEmployeesInfo {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.employeeId = data.employee_id || "";
    this.employeeName =
      data.employee_id && data.employee_name
        ? `${data.employee_id} - ${data.employee_name}`
        : data.employee_name || "";
  }

  // Selecting an employee, the data should come in as an object with employee_id and employee_name
  selectEmployee(employee) {
    if (!employee) {
      this.employeeId = null;
      this.employeeName = null;
    } else {
      this.employeeId = employee.employee_id;
      this.employeeName = employee.employee_name;
    }
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.rowId,
      employee_id: this.employeeId,
    };
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields, jsaId, action = "update") {
    const patch = {
      patch_type: "jsa_training_employees",
      jsa_id: jsaId,
      id: this.rowId,
      action,
    };

    // Add employee_id if provided in fields
    if (fields.employeeId !== undefined) {
      patch.employee_id = fields.employeeId;
    }

    return patch;
  }
}

// JSA Training Info
export class JsaTrainingInfo {
  constructor(data = {}) {
    // Checkboxes
    this.jobTrainingCompletedYes = data.job_training_completed_yes || false;
    this.jobTrainingCompletedNo = data.job_training_completed_no || false;
    this.handsOnJobTrainingYes = data.hands_on_job_training_yes || false;
    this.handsOnJobTrainingNo = data.hands_on_job_training_no || false;
    // Text fields
    this.ewiTrainerName = data.ewi_trainer_name || "";
    this.ewiTrainerId = data.ewi_trainer_id || "";
    this.nonEwiTrainerName = data.non_ewi_trainer_name || "";
    this.trainingInformation = data.training_information || "";
    // EWI Trainer checkboxes, wether the box is checked or not.
    const hasEwiTrainerId =
      this.ewiTrainerId && this.ewiTrainerId.trim() !== "";
    const hasNonEwiTrainerName =
      this.nonEwiTrainerName && this.nonEwiTrainerName.trim() !== "";

    this.ewiTrainerYes = hasEwiTrainerId;
    this.ewiTrainerNo = !hasEwiTrainerId && hasNonEwiTrainerName;
    // List of employees that will be included in the training
    this.jsaTrainingEmployeesInfo = data.training_employees
      ? data.training_employees.map(
          (employee) => new JsaTrainingEmployeesInfo(employee),
        )
      : [];
  }

  // Training list employees functions
  // Adding a row to the JSA training employees info
  addBlankJsaTrainingEmployeesInfo() {
    const newRow = new JsaTrainingEmployeesInfo();
    this.jsaTrainingEmployeesInfo.push(newRow);
    return newRow;
  }

  // Adding all delData employees to the JSA training employees info
  addDelEmployeesToJsaTrainingEmployeesInfo(delEmployees) {
    // Setting up the existing employee ids in a set to filter out duplicates.
    const existingEmployeeIds = new Set(
      this.jsaTrainingEmployeesInfo.map((emp) => emp.employeeId),
    );
    // Filter del.employees to only get the ones not already in our list
    const newEmployees = delEmployees.filter(
      (employee) => !existingEmployeeIds.has(employee.employeeId),
    );
    // Add only the new employees while preserving all existing ones
    if (newEmployees.length > 0) {
      const newRows = newEmployees.map(
        (employee) =>
          new JsaTrainingEmployeesInfo({
            employee_id: employee.employeeId,
            employee_name: employee.employeeName,
          }),
      );
      this.jsaTrainingEmployeesInfo = [
        ...this.jsaTrainingEmployeesInfo,
        ...newRows,
      ];
      return newRows;
    }
    return [];
  }

  // Removing a row from the JSA training employees info
  removeJsaTrainingEmployeesInfoRow(rowId) {
    this.jsaTrainingEmployeesInfo = this.jsaTrainingEmployeesInfo.filter(
      (row) => row.rowId !== rowId,
    );
  }

  // Updating a row in the JSA training employees info
  updateJsaTrainingEmployeesInfoRow(employee) {
    const targetRow = this.jsaTrainingEmployeesInfo.find(
      (emp) => emp.rowId === employee.rowId,
    );
    if (targetRow) {
      targetRow.employeeId = employee.employeeId;
      targetRow.employeeName = employee.employeeName;
    }
    return targetRow;
  }

  // Functions to update the training info
  updateJobTrainingCompletedYes(value) {
    if (value) {
      this.jobTrainingCompletedYes = true;
      this.jobTrainingCompletedNo = false;
    } else {
      this.jobTrainingCompletedYes = false;
    }
  }

  updateJobTrainingCompletedNo(value) {
    if (value) {
      this.jobTrainingCompletedNo = true;
      this.jobTrainingCompletedYes = false;
    } else {
      this.jobTrainingCompletedNo = false;
    }
  }

  // Handson job training functions, this will dictate if any of the text fields are shown
  updateHandsOnJobTrainingYes(value) {
    if (value) {
      this.handsOnJobTrainingYes = true;
      this.handsOnJobTrainingNo = false;
    } else {
      this.handsOnJobTrainingYes = false;
      this.ewiTrainerName = "";
      this.ewiTrainerId = "";
      this.nonEwiTrainerName = "";
      this.trainingInformation = "";
      this.jsaTrainingEmployeesInfo = [];
    }
  }

  updateHandsOnJobTrainingNo(value) {
    if (value) {
      this.handsOnJobTrainingNo = true;
      this.handsOnJobTrainingYes = false;
      this.ewiTrainerName = "";
      this.ewiTrainerId = "";
      this.nonEwiTrainerName = "";
      this.trainingInformation = "";
      this.jsaTrainingEmployeesInfo = [];
    } else {
      this.handsOnJobTrainingNo = false;
    }
  }

  updateEwiTrainerYes(value) {
    if (value) {
      this.ewiTrainerYes = true;
      this.ewiTrainerNo = false;
      this.nonEwiTrainerName = "";
    } else {
      this.ewiTrainerYes = false;
      this.ewiTrainerName = "";
      this.ewiTrainerId = "";
    }
  }

  updateEwiTrainerNo(value) {
    if (value) {
      this.ewiTrainerNo = true;
      this.ewiTrainerYes = false;
      this.ewiTrainerName = "";
      this.ewiTrainerId = "";
    } else {
      this.ewiTrainerNo = false;
    }
  }

  updateEwiTrainerInfo(trainerInfo) {
    if (!trainerInfo) {
      this.ewiTrainerName = "";
      this.ewiTrainerId = "";
    } else {
      this.ewiTrainerName = trainerInfo.label;
      this.ewiTrainerId = trainerInfo.value;
    }
  }

  updateNonEwiTrainerName(trainerName) {
    this.nonEwiTrainerName = trainerName;
  }

  updateTrainingInformation(trainingInformation) {
    this.trainingInformation = trainingInformation;
  }

  // To json for sending to the database
  toJson() {
    return {
      job_training_completed_yes: this.jobTrainingCompletedYes,
      job_training_completed_no: this.jobTrainingCompletedNo,
      hands_on_job_training_yes: this.handsOnJobTrainingYes,
      hands_on_job_training_no: this.handsOnJobTrainingNo,
      ewi_trainer_name: this.ewiTrainerName,
      ewi_trainer_id: this.ewiTrainerId,
      non_ewi_trainer_name: this.nonEwiTrainerName,
      training_information: this.trainingInformation,
    };
  }

  // toJson for the training employees info
  toJsonTrainingEmployeesInfo() {
    // Checking if the checkbox for on the job training is checked
    if (this.handsOnJobTrainingYes) {
      return this.jsaTrainingEmployeesInfo.map((emp) => emp.toJson());
    } else {
      return [];
    }
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields, jsaId, action = "update") {
    const fieldMap = {
      jobTrainingCompletedYes: "job_training_completed_yes",
      jobTrainingCompletedNo: "job_training_completed_no",
      handsOnJobTrainingYes: "hands_on_job_training_yes",
      handsOnJobTrainingNo: "hands_on_job_training_no",
      ewiTrainerName: "ewi_trainer_name",
      ewiTrainerId: "ewi_trainer_id",
      nonEwiTrainerName: "non_ewi_trainer_name",
      trainingInformation: "training_information",
    };

    const patch = {
      patch_type: "jsa_training",
      jsa_id: jsaId,
      action,
    };

    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }

    return patch;
  }
}
